// Client side of configuration (Phase 1): typed wrappers over the config callables, and live,
// limited watchers over the config collections. Clients never write config documents directly:
// every change goes through `saveConfig`, which validates, checks permission and versions it.
import { ulid } from 'ulid';
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type DocumentData,
  type Firestore,
  type Timestamp,
} from 'firebase/firestore';
import { httpsCallable, type Functions } from 'firebase/functions';
import { makeBoxBarcode, type QuoteFailure, type QuoteResult } from '@ecobox/domain';
import {
  CONFIG_KINDS,
  type ConfigKindName,
  type ConfigRecord,
  type CreateStaffInput,
  type UpdateStaffInput,
} from '@ecobox/schema';
import { parseStaffProfile, type StaffProfile } from './auth';

// ---- errors ------------------------------------------------------------------

export type ConfigErrorCode =
  'conflict' | 'permission' | 'sign_in' | 'invalid' | 'not_found' | 'offline' | 'unknown';

/** A callable failed. `message` is always safe to show to the person. */
export class ConfigCallError extends Error {
  readonly code: ConfigErrorCode;
  constructor(code: ConfigErrorCode, message: string) {
    super(message);
    this.name = 'ConfigCallError';
    this.code = code;
  }
}

export const CONFLICT_MESSAGE =
  'Someone else changed this — reload the page, then make your change again.';

function serverMessage(err: unknown): string {
  return typeof err === 'object' && err !== null && 'message' in err
    ? String((err as { message: unknown }).message).trim()
    : '';
}

/** Turns a Firebase Functions error into a plain-English `ConfigCallError`. */
export function toConfigError(err: unknown): ConfigCallError {
  if (err instanceof ConfigCallError) return err;
  const raw =
    typeof err === 'object' && err !== null && 'code' in err
      ? String((err as { code: unknown }).code)
      : '';
  const code = raw.replace(/^functions\//, '');
  const message = serverMessage(err);
  // The Functions SDK uses the bare code as the message when the server sent none.
  const readable = message && message.toLowerCase() !== code.toLowerCase() ? message : '';
  switch (code) {
    case 'aborted':
      return new ConfigCallError('conflict', CONFLICT_MESSAGE);
    case 'permission-denied':
      return new ConfigCallError(
        'permission',
        readable || 'You do not have permission to do this. Ask a super admin.',
      );
    case 'unauthenticated':
      return new ConfigCallError('sign_in', 'Your sign-in has expired. Sign in again.');
    case 'invalid-argument':
    case 'failed-precondition':
    case 'already-exists':
    case 'out-of-range':
      return new ConfigCallError('invalid', readable || 'The server did not accept that.');
    case 'not-found':
      return new ConfigCallError('not_found', readable || 'That record no longer exists.');
    case 'unavailable':
    case 'deadline-exceeded':
      return new ConfigCallError(
        'offline',
        'Could not reach the server. Check the connection and try again.',
      );
    default:
      return new ConfigCallError('unknown', readable || 'Something went wrong. Try again.');
  }
}

async function call<I, O>(functions: Functions, name: string, data: I): Promise<O> {
  try {
    return (await httpsCallable<I, O>(functions, name)(data)).data;
  } catch (err) {
    throw toConfigError(err);
  }
}

// ---- callables ---------------------------------------------------------------

export interface SaveConfigInput {
  kind: ConfigKindName;
  record: Record<string, unknown>;
  /** The `docVersion` the editor saw. Omit for a new record. */
  expectedVersion?: number | undefined;
}
export interface SaveConfigResult {
  id: string;
  docVersion: number;
}

export function saveConfig(
  functions: Functions,
  input: SaveConfigInput,
): Promise<SaveConfigResult> {
  const { expectedVersion, ...rest } = input;
  return call(
    functions,
    'saveConfig',
    expectedVersion === undefined ? rest : { ...rest, expectedVersion },
  );
}

export function deleteConfig(
  functions: Functions,
  input: { kind: ConfigKindName; id: string },
): Promise<unknown> {
  return call(functions, 'deleteConfig', input);
}

/** A new customer or agent numbering series id (03 §3.5); a branch's default series uses the branch code. */
export function newNumberSeriesId(): string {
  return `nsr_${ulid()}`;
}

/** A new agent id (03 §3.5). */
export function newAgentId(): string {
  return `agt_${ulid()}`;
}

/** Per numbering series: numbers leased to devices and shipments saved with one (03 §3.5). */
export interface NumberingUsage {
  usage: Record<string, { leased: number; used: number }>;
}

export function numberingUsage(functions: Functions): Promise<NumberingUsage> {
  return call(functions, 'numberingUsage', {});
}

/** Copies a draft into a new active version and retires the previous one (D-09). */
export function retireRateCard(functions: Functions, id: string): Promise<unknown> {
  return call(functions, 'retireRateCard', { id });
}

export function publishRateCard(functions: Functions, id: string): Promise<unknown> {
  return call(functions, 'publishRateCard', { id });
}

/** What `generateBarcodeBatch` returns: the sequence numbers, the codes they make, and a preview. */
export interface BarcodeBatchResult {
  batchId: string;
  from: number;
  to: number;
  /** The first and last barcode of the batch, like EB0000000018. Show these, not `from`/`to`. */
  fromCode: string;
  toCode: string;
  /** The first 20 codes, for a quick look. */
  codes: string[];
}

export function generateBarcodeBatch(
  functions: Functions,
  input: { count: number; issuedToBranchId?: string | undefined },
): Promise<BarcodeBatchResult> {
  const { count, issuedToBranchId } = input;
  return call(
    functions,
    'generateBarcodeBatch',
    issuedToBranchId ? { count, issuedToBranchId } : { count },
  );
}

export interface PerBoxCostImportRow {
  destination: string;
  sizeId?: string | undefined;
  forwardingMinor: number;
  deliveryMinor: number;
  currency: string;
  partnerId?: string | undefined;
}

export function importPerBoxCosts(
  functions: Functions,
  input: { costModelId: string; rows: PerBoxCostImportRow[] },
): Promise<unknown> {
  return call(functions, 'importPerBoxCosts', input);
}

export interface QuoteShipmentInput {
  originBranchId: string;
  service: 'sea' | 'air' | 'prime';
  destinationCityId: string;
  boxes: {
    sizeId: string;
    weightKg?: number | undefined;
    dimsCm?: { l: number; w: number; h: number } | undefined;
  }[];
  declaredValueMinor?: number | undefined;
  addOnCodes?: string[] | undefined;
}

/** Prices a shipment on the server with the active rate cards. */
export function quoteShipment(
  functions: Functions,
  input: QuoteShipmentInput,
): Promise<QuoteResult | QuoteFailure> {
  return call(functions, 'quoteShipment', input);
}

export function createStaff(
  functions: Functions,
  input: CreateStaffInput,
): Promise<{ uid: string }> {
  return call(functions, 'createStaff', input);
}

export function updateStaff(
  functions: Functions,
  input: UpdateStaffInput,
): Promise<{ uid: string }> {
  return call(functions, 'updateStaff', input);
}

export function deactivateStaff(functions: Functions, uid: string): Promise<{ uid: string }> {
  return call(functions, 'deactivateStaff', { uid });
}

/** The signed-in person replaces a temporary password with their own (07 §7.2). */
export function changeOwnPassword(
  functions: Functions,
  password: string,
): Promise<{ uid: string }> {
  return call(functions, 'changeOwnPassword', { password });
}

// ---- watchers ----------------------------------------------------------------

/** Config lists are small; destination cities are the one big list. Every query is limited (02 §2.3). */
export const CONFIG_LIST_LIMIT = 500;
export const CONFIG_LIMITS: Partial<Record<ConfigKindName, number>> = { destinationCity: 2000 };

export function configLimit(kind: ConfigKindName): number {
  return CONFIG_LIMITS[kind] ?? CONFIG_LIST_LIMIT;
}

/** One config document: the record as validated by the schema, plus what the server tracks. */
export interface ConfigEntry<K extends ConfigKindName = ConfigKindName> {
  /** The document id (what `CONFIG_KINDS[kind].idOf` gives for the record). */
  id: string;
  record: ConfigRecord<K>;
  /** Send this back as `expectedVersion` when saving. */
  docVersion: number;
  updatedAt: string | null;
  updatedBy: string | null;
}

const META_FIELDS = ['updatedAt', 'updatedBy', 'docVersion'] as const;

function toIso(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as Timestamp).toDate().toISOString();
  }
  return null;
}

/** Separates the server's bookkeeping fields from the record (the schemas are strict). */
export function toConfigEntry<K extends ConfigKindName>(
  id: string,
  data: DocumentData,
): ConfigEntry<K> {
  const record: Record<string, unknown> = { ...data };
  for (const f of META_FIELDS) delete record[f];
  return {
    id,
    record: record as ConfigRecord<K>,
    docVersion: typeof data['docVersion'] === 'number' ? data['docVersion'] : 0,
    updatedAt: toIso(data['updatedAt']),
    updatedBy: typeof data['updatedBy'] === 'string' ? data['updatedBy'] : null,
  };
}

/** Live list of one config collection, at most `max` documents (default per kind). */
export function watchConfigList<K extends ConfigKindName>(
  db: Firestore,
  kind: K,
  onData: (entries: ConfigEntry<K>[]) => void,
  onError: (err: Error) => void,
  max: number = configLimit(kind),
): () => void {
  const q = query(collection(db, CONFIG_KINDS[kind].collection), limit(max));
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map((d) => toConfigEntry<K>(d.id, d.data()))),
    onError,
  );
}

/** Live view of one config document, or null if it does not exist. */
export function watchConfigDoc<K extends ConfigKindName>(
  db: Firestore,
  kind: K,
  id: string,
  onData: (entry: ConfigEntry<K> | null) => void,
  onError: (err: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, CONFIG_KINDS[kind].collection, id),
    (snap) => onData(snap.exists() ? toConfigEntry<K>(snap.id, snap.data()) : null),
    onError,
  );
}

// ---- staff and roles -----------------------------------------------------------

export interface StaffEntry extends StaffProfile {
  uid: string;
}

export const STAFF_LIST_LIMIT = 500;

/** Needs the `users.manage` permission (the rules let a super admin read `staff`). */
export function watchStaffList(
  db: Firestore,
  onData: (staff: StaffEntry[]) => void,
  onError: (err: Error) => void,
  max = STAFF_LIST_LIMIT,
): () => void {
  return onSnapshot(
    query(collection(db, 'staff'), limit(max)),
    (snap) =>
      onData(
        snap.docs.flatMap((d) => {
          const profile = parseStaffProfile(d.data());
          return profile ? [{ uid: d.id, ...profile }] : [];
        }),
      ),
    onError,
  );
}

export interface RoleEntry {
  id: string;
  name: string;
  permissions: string[];
  /** The role of an AI worker account, rewritten at each approval of its connection. */
  bot?: boolean;
}

/** Roles a super admin has saved; these override the built-in role of the same id. */
export function watchRoles(
  db: Firestore,
  onData: (roles: RoleEntry[]) => void,
  onError: (err: Error) => void,
  max = 100,
): () => void {
  return onSnapshot(
    query(collection(db, 'roles'), limit(max)),
    (snap) =>
      onData(
        snap.docs.map((d) => {
          const data = d.data();
          const permissions: unknown[] = Array.isArray(data['permissions'])
            ? data['permissions']
            : [];
          return {
            id: d.id,
            name: typeof data['name'] === 'string' ? data['name'] : d.id,
            permissions: permissions.filter((p): p is string => typeof p === 'string'),
            ...(data['bot'] === true ? { bot: true } : {}),
          };
        }),
      ),
    onError,
  );
}

// ---- barcodes ------------------------------------------------------------------

export interface BarcodeBatchRow {
  id: string;
  from: string | null;
  to: string | null;
  count: number | null;
  issuedToBranchId: string | null;
  status: string | null;
  createdAt: string | null;
}

function textOrNull(v: unknown): string | null {
  return typeof v === 'string' ? v : typeof v === 'number' ? String(v) : null;
}

/** A batch stores its range as sequence numbers; staff read barcodes (EB0000000018), not numbers. */
function batchCode(code: unknown, seq: unknown): string | null {
  if (typeof code === 'string') return code;
  if (typeof seq !== 'number' || !Number.isInteger(seq) || seq < 0 || seq > 999_999_999)
    return null;
  return makeBoxBarcode(seq);
}

export function toBarcodeBatch(id: string, data: DocumentData): BarcodeBatchRow {
  return {
    id,
    from: batchCode(data['fromCode'] ?? data['from'], data['fromNumber']),
    to: batchCode(data['toCode'] ?? data['to'], data['toNumber']),
    count: typeof data['count'] === 'number' ? data['count'] : null,
    issuedToBranchId: textOrNull(data['issuedToBranchId']),
    status: textOrNull(data['status']),
    createdAt: toIso(data['createdAt'] ?? data['generatedAt']),
  };
}

/** The latest batches, newest first (batch ids sort by time). */
export function watchBarcodeBatches(
  db: Firestore,
  onData: (batches: BarcodeBatchRow[]) => void,
  onError: (err: Error) => void,
  max = 30,
): () => void {
  return onSnapshot(
    query(collection(db, 'barcodeBatches'), orderBy(documentId(), 'desc'), limit(max)),
    (snap) => onData(snap.docs.map((d) => toBarcodeBatch(d.id, d.data()))),
    onError,
  );
}

export interface BarcodeRow {
  code: string;
  status: string;
  branchId: string | null;
  batchId: string | null;
}

function toBarcode(id: string, data: DocumentData): BarcodeRow {
  return {
    code: textOrNull(data['code']) ?? id,
    status: textOrNull(data['status']) ?? 'unknown',
    branchId: textOrNull(data['branchId']),
    batchId: textOrNull(data['batchId']),
  };
}

/** One barcode by its code, or null if no such code was ever generated. */
export async function getBarcode(db: Firestore, code: string): Promise<BarcodeRow | null> {
  const snap = await getDoc(doc(db, 'barcodes', code.trim().toUpperCase()));
  return snap.exists() ? toBarcode(snap.id, snap.data()) : null;
}

export const BARCODE_BATCH_MAX = 5000;

/** Every code in a batch (at most 5,000, the most a batch can hold), in code order. */
export async function getBarcodesInBatch(db: Firestore, batchId: string): Promise<string[]> {
  const snap = await getDocs(
    query(collection(db, 'barcodes'), where('batchId', '==', batchId), limit(BARCODE_BATCH_MAX)),
  );
  return snap.docs.map((d) => toBarcode(d.id, d.data()).code).sort();
}

// ---- proposed changes a person approves (12 §12.4) ------------------------------------------------

export interface PartnerRateDiffRow {
  appliesTo?: string;
  destination: string;
  sizeId?: string;
  basis: 'per_box' | 'per_kg';
  before?: { amountMinor: number; currency: string };
  after?: { amountMinor: number; currency: string };
}

/** A change to a partner's rates proposed by the back-office agent, waiting for a person. */
export interface ConfigProposal {
  id: string;
  kind: 'partner';
  targetId: string;
  targetName: string;
  baseVersion: number;
  diff: PartnerRateDiffRow[];
  counts: { added: number; changed: number; removed: number };
  note: string;
  makerUid: string;
  makerName: string;
  status: 'pending' | 'approved' | 'rejected' | 'superseded' | 'stale';
  createdAt: string;
  decidedBy?: string;
  decidedAt?: string;
  decisionNote?: string;
}

export function listConfigProposals(
  functions: Functions,
  input: { status?: 'pending' | 'all'; partnerId?: string } = {},
): Promise<{ proposals: ConfigProposal[] }> {
  return call(functions, 'listConfigProposals', input);
}

export function decideConfigProposal(
  functions: Functions,
  input: { proposalId: string; decision: 'approve' | 'reject'; note?: string },
): Promise<{ status: ConfigProposal['status']; docVersion?: number }> {
  return call(functions, 'decideConfigProposal', input);
}
